import {
  Component,
  HostBinding,
  Inject,
  InjectionToken,
  Input,
  OnInit,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { DepreciationMethodId } from '@assetaccountant/api/models/depreciation-method-id';
import {
  DepreciationMethodDtoWithKey,
  DepreciationMethodKey,
} from '@assetaccountant/core/models/depreciation-method';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

export const DEPRECIATION_METHOD_MODIFIER = new InjectionToken<DepreciationMethodModifierType[]>(
  'DepreciationMethodModifier',
);

export type DepreciationMethodModifierType = Type<DepreciationMethodModifier> & {
  readonly regimeId: number;
};

export interface DepreciationMethodModifier {
  isVisible: boolean;
  init?: (initialMethod: DepreciationMethodDtoWithKey | undefined) => void;
  applyFilter(
    depreciationMethods$: Observable<DepreciationMethodDtoWithKey[]>,
    acquisitionDate$: Observable<string | null>,
    firstUseDate$: Observable<string | null>,
    isFirstUse$: Observable<boolean>,
    effectiveDate$: Observable<string | null>,
  ): Observable<DepreciationMethodDtoWithKey[]>;
  getNewMethodCode?: (oldMethodCode: string) => string;
}

@Component({
  selector: 'app-depreciation-method-modifier',
  templateUrl: './depreciation-method-modifier.component.html',
  styleUrls: ['./depreciation-method-modifier.component.scss'],
  standalone: false,
})
export class DepreciationMethodModifierComponent implements OnInit {
  @HostBinding('class') public get cssClass() {
    return this.components.some(modifier => modifier.isVisible) ? 'd-block' : '';
  }

  @Input() regimeId: number;
  @Input() initialDepreciationMethodId?: DepreciationMethodId | DepreciationMethodKey | null;

  private depreciationMethods$ = new BehaviorSubject<DepreciationMethodDtoWithKey[]>([]);
  @Input() set depreciationMethods(value: DepreciationMethodDtoWithKey[]) {
    this.depreciationMethods$.next(value);
  }

  private acquisitionDate$ = new BehaviorSubject<string | null>(null);
  @Input() set acquisitionDate(value: string | null | undefined) {
    this.acquisitionDate$.next(value ?? null);
  }
  private firstUseDate$ = new BehaviorSubject<string | null>(null);
  @Input() set firstUseDate(value: string | null | undefined) {
    this.firstUseDate$.next(value ?? null);
  }
  private isFirstUse$ = new BehaviorSubject<boolean>(true);
  @Input() set isFirstUse(value: boolean) {
    this.isFirstUse$.next(value);
  }
  private effectiveDate$ = new BehaviorSubject<string | null>(null);
  @Input() set effectiveDate(value: string | null | undefined) {
    this.effectiveDate$.next(value ?? null);
  }

  filteredDepreciationMethods$: Observable<DepreciationMethodDtoWithKey[]>;

  private components: DepreciationMethodModifier[] = [];

  constructor(
    private viewContainerRef: ViewContainerRef,
    @Inject(DEPRECIATION_METHOD_MODIFIER) private modifierTypes: DepreciationMethodModifierType[],
  ) {}

  ngOnInit() {
    const initialMethod = this.depreciationMethods$
      .getValue()
      .find(dm => dm.id === this.initialDepreciationMethodId);

    this.modifierTypes.forEach(modifierType => {
      if (modifierType.regimeId === this.regimeId) {
        const modifier = this.viewContainerRef.createComponent(modifierType).instance;
        modifier.init?.(initialMethod);

        this.components.push(modifier);
      } else if (typeof modifierType.regimeId !== 'number') {
        throw new Error(`Class ${modifierType.name} must have a static regimeId`);
      }
    });

    this.filteredDepreciationMethods$ = this.components
      .reduce(
        (methods, modifier) =>
          modifier.applyFilter(
            methods,
            this.acquisitionDate$,
            this.firstUseDate$,
            this.isFirstUse$,
            this.effectiveDate$,
          ),
        this.depreciationMethods$.asObservable(),
      )
      // QueryList causes this to be re-subscribed to - so use shareReplay to resume, not restart
      .pipe(
        shareReplay(1),
        map(methods =>
          methods.filter(m => m.isActive || m.id === this.initialDepreciationMethodId),
        ),
      );
  }

  getNewMethodCode(oldMethodCode: string) {
    const newMethodCode = this.components.reduce(
      (code, modifier) => (modifier.getNewMethodCode ? modifier.getNewMethodCode(code) : code),
      oldMethodCode,
    );

    return newMethodCode;
  }
}
